import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { api, API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Send, Plus, Trash2, MessageSquare, Sparkles, History, Copy, Check, Search as SearchIcon,
  Circle, Square, Globe, Paperclip, X, ExternalLink,
  Camera, Image as ImageIcon, Video, Film, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useJixxeoRecorder } from '../context/RecorderContext';
import PromptChips from '../components/jixxeo/PromptChips';
import WeatherCard from '../components/jixxeo/WeatherCard';
import ThinkingIndicator from '../components/jixxeo/ThinkingIndicator';

const TOOL_MAP = {
  chat: { title: 'AI Chat', placeholder: 'Ask Jixxeo AI anything…' },
  writing: { title: 'Writing Assistant', placeholder: 'What should I write?' },
  coding: { title: 'Coding Assistant', placeholder: 'Describe what to build…' },
  homework: { title: 'Homework Helper', placeholder: 'Homework question…' },
  research: { title: 'Research', placeholder: 'What do you want to research?' },
};

function ConversationList({ convs, activeId, onOpen, onDelete, onRename, onNew, query, setQuery }) {
  return (
    <>
      <Button data-testid="new-chat-btn" onClick={onNew} className="w-full gradient-brand text-white mb-2"><Plus className="w-4 h-4 mr-1" /> New chat</Button>
      <div className="relative mb-3">
        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
        <input
          data-testid="conv-search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search conversations"
          className="w-full pl-8 pr-3 py-2 text-sm rounded-md bg-white/5 border border-white/10 focus:border-violet-500 outline-none"
        />
      </div>
      <ScrollArea className="flex-1 max-h-[65vh]">
        {convs.length === 0 && <div className="text-xs text-slate-500 p-3 text-center">No conversations yet</div>}
        {convs.map(c => (
          <div key={c.id}
               className={`group flex items-center gap-2 p-2 rounded-lg text-sm ${activeId === c.id ? 'bg-violet-500/20 text-violet-200' : 'hover:bg-white/5'}`}
               data-testid={`conv-${c.id}`}>
            <button className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer" onClick={() => onOpen(c.id)}>
              <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="flex-1 truncate text-left">{c.title || 'Untitled'}</span>
            </button>
            <button
              className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/10 sm:opacity-0 sm:group-hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); const t = prompt('Rename:', c.title); if (t) onRename(c.id, t); }}
              data-testid={`rename-${c.id}`}
              title="Rename"
              aria-label="Rename conversation"
            >✎</button>
            <button
              className="p-1 rounded text-rose-400 hover:text-rose-200 hover:bg-rose-500/20 sm:opacity-0 sm:group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete "${c.title || 'Untitled'}"? This cannot be undone.`)) onDelete(c.id);
              }}
              data-testid={`delete-${c.id}`}
              title="Delete conversation"
              aria-label="Delete conversation"
            ><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </ScrollArea>
    </>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(text || ''); }
    catch { /* fallback */ const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      data-testid="msg-copy-btn"
      onClick={onCopy}
      aria-label={copied ? 'Copied' : 'Copy response'}
      className={`absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors
        ${copied ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-300' : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}
    >
      {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
    </button>
  );
}

function AssistantMessage({ text, sources, weather, image }) {
  return (
    <div className="flex justify-start">
      <div data-testid="msg-assistant" className="relative max-w-[85%] px-4 pt-7 pb-3 rounded-2xl bg-white/5 border border-white/10 rounded-bl-sm">
        <CopyButton text={text} />
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{text}</div>
        {image && image.url && (
          <a
            href={image.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`chat-image-${image.id}`}
            className="mt-3 block rounded-xl overflow-hidden border border-white/15 shadow-lg hover:scale-[1.01] transition-transform"
            title={image.prompt || 'Open full-size'}
          >
            <img src={image.url} alt={image.prompt || 'Generated image'} className="w-full h-auto block" />
            {image.prompt && (
              <div className="px-3 py-2 text-[11px] text-slate-400 bg-slate-950/60">
                <span className="font-semibold text-slate-200">Prompt:</span> {image.prompt}
              </div>
            )}
          </a>
        )}
        {weather && <WeatherCard data={weather} />}
        {sources && sources.length > 0 && (
          <div data-testid="msg-sources" className="mt-3 pt-2 border-t border-white/10 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Sources</div>
            {sources.slice(0, 5).map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 text-xs text-violet-300 hover:text-violet-200 hover:underline">
                <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" /><span className="truncate">{s.title || s.url}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ChatPage({ tool = 'chat' }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(params.get('cid') || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [thinkingStartedAt, setThinkingStartedAt] = useState(null);
  const [thinkingLabel, setThinkingLabel] = useState(null);
  const [streamSources, setStreamSources] = useState([]);
  const [streamWeather, setStreamWeather] = useState(null);
  const [streamImage, setStreamImage] = useState(null);
  const [query, setQuery] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{data_url, mime, name, kind, duration, thumbnail}]
  const [webSearch, setWebSearch] = useState(false);
  const [kbInset, setKbInset] = useState(0); // Android keyboard offset
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState(''); // "Uploading…" | "Analyzing video…" | ""
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);        // gallery (image)
  const cameraInputRef = useRef(null);      // camera (image)
  const videoFileRef = useRef(null);        // gallery (video)
  const videoCamRef = useRef(null);         // camera (video)
  const fileAnyRef = useRef(null);          // any file (txt, pdf, etc.)
  const dropRef = useRef(null);
  const recorder = useJixxeoRecorder();

  const cfg = TOOL_MAP[tool] || TOOL_MAP.chat;

  const loadConvs = async () => {
    if (!user) return;
    const r = await api.get('/chat/conversations');
    setConvs(r.data.filter(c => (c.tool || 'chat') === tool));
  };

  useEffect(() => { loadConvs(); /* eslint-disable-next-line */ }, [user, tool]);

  // Pre-fill from home ask bar (?q=…)
  useEffect(() => {
    const q = params.get('q');
    if (q && !input) {
      setInput(q);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    api.get(`/chat/conversations/${activeId}/messages`).then(r => setMessages(r.data)).catch(() => {});
  }, [activeId]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e6, behavior: 'smooth' }); }, [messages, streamText]);

  // Auto-grow textarea
  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 240);
    el.style.height = next + 'px';
  };
  useEffect(() => { autoGrow(); }, [input]);

  // Visual Viewport — keep composer above Android keyboard
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      setKbInset(inset);
      // keep latest message visible when keyboard opens
      requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: 1e6, behavior: 'smooth' }));
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, []);

  const filtered = query
    ? convs.filter(c => (c.title || '').toLowerCase().includes(query.toLowerCase()))
    : convs;

  const pickFiles = () => fileInputRef.current?.click();

  const uploadOneImage = async (f) => {
    const fd = new FormData();
    fd.append('file', f);
    const token = localStorage.getItem('jixxeo_token');
    const resp = await fetch(`${API}/chat/upload-image`, { method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) {
      let msg = 'upload failed';
      try { msg = (await resp.json()).detail || msg; } catch {}
      throw new Error(msg);
    }
    return resp.json();
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const ok = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    setUploadingLabel('Uploading photo…');
    try {
      for (const f of files) {
        const type = f.type || '';
        if (!ok.includes(type) && !/\.(heic|heif)$/i.test(f.name)) {
          toast.error(`${f.name}: unsupported type`); continue;
        }
        if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: too large (max 8MB)`); continue; }
        try {
          const data = await uploadOneImage(f);
          setAttachments(a => [...a, { kind: 'image', data_url: data.data_url, mime: data.mime, name: f.name, size: f.size }]);
        } catch (err) {
          toast.error(`Couldn't attach ${f.name}: ${err.message}`);
        }
      }
    } finally { setUploadingLabel(''); }
  };

  const handleVideoFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    for (const f of files) {
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name}: video too large (max 50MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      setUploadingLabel('Analyzing video…');
      try {
        const token = localStorage.getItem('jixxeo_token');
        const resp = await fetch(`${API}/chat/upload-video`, { method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) {
          let msg = 'upload failed';
          try { msg = (await resp.json()).detail || msg; } catch {}
          throw new Error(msg);
        }
        const data = await resp.json();
        // Store the video attachment with its extracted frames so the chat endpoint
        // can forward them to the vision model as image_urls.
        setAttachments(a => [...a, {
          kind: 'video', name: f.name, size: f.size,
          duration: data.duration, mime: data.mime,
          thumbnail: data.thumbnail,
          frames: data.frames,         // [{t, data_url}, …]
        }]);
        toast.success(`Video ready — ${data.frames.length} keyframes extracted for analysis.`);
      } catch (err) {
        toast.error(`Couldn't analyze that video: ${err.message || 'try again'}`);
      } finally { setUploadingLabel(''); }
    }
  };

  const handleAnyFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    for (const f of files) {
      // Route by MIME type so a user can tap "Choose File" and still send an image/video correctly.
      const type = f.type || '';
      if (type.startsWith('image/')) {
        const fake = { target: { files: [f], value: '' } };
        await handleFiles(fake);
      } else if (type.startsWith('video/')) {
        const fake = { target: { files: [f], value: '' } };
        await handleVideoFiles(fake);
      } else if (type === 'text/plain' || /\.(txt|md|json|csv|log)$/i.test(f.name)) {
        if (f.size > 2 * 1024 * 1024) { toast.error('File too large (max 2MB text)'); continue; }
        const text = await f.text();
        setAttachments(a => [...a, { kind: 'text', name: f.name, text, size: f.size }]);
        toast.success(`Attached text file: ${f.name}`);
      } else {
        toast.error(`"${f.name}" isn't a supported type yet. Try an image, video, or text file.`);
      }
    }
  };

  // Paste image from clipboard (desktop)
  useEffect(() => {
    const onPaste = async (e) => {
      if (!user) return;
      const items = Array.from(e.clipboardData?.items || []);
      const imgItems = items.filter(it => it.type && it.type.startsWith('image/'));
      if (!imgItems.length) return;
      e.preventDefault();
      setUploadingLabel('Uploading pasted image…');
      try {
        for (const it of imgItems) {
          const file = it.getAsFile();
          if (!file) continue;
          const data = await uploadOneImage(file);
          setAttachments(a => [...a, { kind: 'image', data_url: data.data_url, mime: data.mime, name: file.name || 'clipboard.png', size: file.size }]);
        }
      } catch (err) {
        toast.error('Could not paste image');
      } finally { setUploadingLabel(''); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [user]);

  const removeAttachment = (i) => setAttachments(a => a.filter((_, k) => k !== i));

  const send = async () => {
    if ((!input.trim() && attachments.length === 0) || streaming) return;
    if (!user) {
      try {
        const { data } = await api.post('/chat/send', { text: input, tool });
        setMessages(prev => [...prev, { id: 'u', role: 'user', text: input }, { id: 'a', role: 'assistant', text: data.reply }]);
        setInput('');
      } catch { toast.error('Failed'); }
      return;
    }
    const userText = input;
    // Build the list of images to send to the vision model.
    //   - image attachments → their data_url
    //   - video attachments → their extracted keyframes (data_urls)
    //   - text attachments  → appended to the user text as a quoted snippet
    const imageUrls = [];
    let extraText = '';
    for (const a of attachments) {
      if (a.kind === 'image') imageUrls.push(a.data_url);
      else if (a.kind === 'video' && Array.isArray(a.frames)) {
        for (const f of a.frames) imageUrls.push(f.data_url);
        extraText += `\n\n(Attached video "${a.name}" · ${a.duration?.toFixed(1)}s · ${a.frames.length} sampled frames follow in chronological order.)`;
      } else if (a.kind === 'text') {
        extraText += `\n\n--- Attached file: ${a.name} ---\n${a.text}\n--- end file ---`;
      }
    }
    const finalText = (userText + extraText).trim();
    setInput('');
    const previews = attachments.map(a => a.kind === 'video' ? a.thumbnail : a.data_url).filter(Boolean);
    setAttachments([]);
    setMessages(prev => [...prev, { id: 'tmp-u', role: 'user', text: userText, image_urls: previews }]);
    setStreaming(true);
    setStreamText('');
    setThinkingStartedAt(Date.now());
    const videoCount = attachments.filter(a => a.kind === 'video').length;
    const imageCount = attachments.filter(a => a.kind === 'image').length;
    setThinkingLabel(
      videoCount > 0 ? 'JixxeoAI is analyzing your video' :
      imageCount > 0 ? 'JixxeoAI is analyzing your image' :
      null
    );
    setStreamSources([]);
    setStreamWeather(null);
    setStreamImage(null);
    // Weather near-me geolocation
    let userLat = null, userLon = null;
    try {
      const needsLoc = /\b(near me|around me|here|my location|outside)\b/i.test(userText)
        && /\b(weather|temperature|forecast|rain|snow|wind|cold|hot|umbrella|jacket)\b/i.test(userText);
      if (needsLoc && navigator.geolocation) {
        const pos = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            p => resolve(p),
            () => resolve(null),
            { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 6000 }
          );
        });
        if (pos) { userLat = pos.coords.latitude; userLon = pos.coords.longitude; }
      }
    } catch {}
    try {
      const token = localStorage.getItem('jixxeo_token');
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = {
        conversation_id: activeId, text: finalText || userText, tool,
        image_urls: imageUrls, web_search: webSearch, timezone: tz,
        user_lat: userLat, user_lon: userLon,
      };
      // Up to 2 retries on transient 5xx / network errors (common on mobile when wifi→cellular switches)
      const ctrl = new AbortController();
      let resp = null;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          resp = await fetch(`${API}/chat/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(payload),
            signal: ctrl.signal,
          });
          if (!resp.ok && resp.status >= 500 && attempt < 2) {
            await new Promise(r => setTimeout(r, 1200));
            continue;
          }
          break;
        } catch (netErr) {
          if (netErr?.name === 'AbortError') throw netErr;
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 1200));
            continue;
          }
          throw netErr;
        }
      }
      if (!resp || !resp.body) throw new Error(`Backend returned ${resp?.status || 'no response'}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let full = '', convId = activeId, finalSources = [], finalWeather = null;
      let buffer = '';
      let chatImage = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE records are terminated by a blank line (\n\n). Hold partials until
        // we see the delimiter so JSON.parse never trips on mid-chunk splits.
        let sep;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const record = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          for (const line of record.split('\n')) {
            if (!line.startsWith('data:')) continue;
            try {
              const data = JSON.parse(line.slice(5).trim());
              if (data.conversation_id && !convId) { convId = data.conversation_id; setActiveId(convId); }
              if (data.sources) { setStreamSources(data.sources); finalSources = data.sources; }
              if (data.weather) { setStreamWeather(data.weather); finalWeather = data.weather; }
              if (data.image) { chatImage = data.image; setStreamImage(data.image); }
              if (data.delta) { full += data.delta; setStreamText(full); }
              if (data.done) {
                setMessages(prev => [...prev, {
                  id: 'tmp-a', role: 'assistant', text: full,
                  sources: finalSources, weather: finalWeather,
                  image: chatImage,
                }]);
                setStreamText(''); setStreamSources([]); setStreamWeather(null); setStreamImage(null);
              }
            } catch (parseErr) { /* keep streaming — tolerate a malformed event */ }
          }
        }
      }
      await loadConvs();
    } catch (err) {
      // Surface the real failure to the user instead of a vague "busy" fallback.
      const msg = err?.name === 'AbortError'
        ? 'Message cancelled.'
        : 'Could not reach Jixxeo AI. Check your connection and tap Send again.';
      toast.error(msg);
      setMessages(prev => [...prev, { id: 'err', role: 'assistant', text: `_${msg}_`, error: true }]);
    }
    finally { setStreaming(false); setThinkingLabel(null); setThinkingStartedAt(null); }
  };

  const newChat = () => { setActiveId(null); setMessages([]); setAttachments([]); setHistoryOpen(false); };
  const openConv = (id) => { setActiveId(id); setHistoryOpen(false); };
  const delConv = async (id) => {
    if (!window.confirm('Delete this conversation?')) return;
    await api.delete(`/chat/conversations/${id}`);
    setConvs(convs.filter(c => c.id !== id));
    if (activeId === id) newChat();
    toast.success('Deleted');
  };
  const renameConv = async (id, title) => {
    await api.post(`/chat/conversations/${id}/rename`, { title });
    setConvs(convs.map(c => c.id === id ? { ...c, title } : c));
  };

  const onKeyDown = (e) => {
    // Enter inserts a newline by default. Only Ctrl/Cmd+Enter OR plain Enter on desktop with no Shift sends.
    // Spec: "Pressing Enter should create a new line." So we only send on Ctrl/Cmd+Enter OR click the send button.
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-10rem)]">
      <Card className="glass p-3 hidden lg:flex flex-col">
        <ConversationList
          convs={filtered} activeId={activeId} onOpen={openConv}
          onDelete={delConv} onRename={renameConv} onNew={newChat}
          query={query} setQuery={setQuery}
        />
      </Card>

      <Card className="glass flex flex-col overflow-hidden">
        <div className="p-3 sm:p-4 border-b border-white/10 flex items-center justify-between gap-2">
          <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
            <SheetTrigger asChild>
              <Button data-testid="open-history-btn" size="icon" variant="ghost" className="lg:hidden shrink-0"><History className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[86%] max-w-sm glass border-white/10 flex flex-col p-4">
              <SheetHeader><SheetTitle className="font-display text-lg">Chat History</SheetTitle></SheetHeader>
              <div className="mt-3 flex-1 flex flex-col min-h-0">
                <ConversationList
                  convs={filtered} activeId={activeId} onOpen={openConv}
                  onDelete={delConv} onRename={renameConv} onNew={newChat}
                  query={query} setQuery={setQuery}
                />
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base sm:text-lg truncate">{cfg.title}</div>
            <div className="text-xs text-slate-400">Powered by Jixxeo AI{user ? ` • ${convs.length} saved` : ''}</div>
          </div>

          {user && <Button data-testid="new-chat-header" size="sm" variant="ghost" onClick={newChat} className="shrink-0"><Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">New</span></Button>}
          {!user && <Link to="/register" className="text-xs text-violet-400 hover:underline shrink-0">Sign up</Link>}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" style={{ paddingBottom: kbInset ? `${kbInset + 12}px` : undefined }}>
          {messages.length === 0 && !streamText && (
            <div className="text-center py-10 sm:py-16 space-y-6">
              <div>
                <Sparkles className="w-12 h-12 mx-auto text-violet-400 mb-3" />
                <div className="font-display text-xl font-bold">Start a conversation</div>
                <div className="text-slate-400 text-sm mt-1">{cfg.placeholder}</div>
              </div>
              <div className="max-w-md mx-auto px-2 text-left">
                <PromptChips
                  rotateMs={6000}
                  title="Try one of these"
                  onPick={(p) => { setInput(p.text); setTimeout(() => textareaRef.current?.focus(), 30); }}
                />
              </div>

              <details data-testid="chat-howto" className="max-w-md mx-auto rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left text-xs text-slate-300 open:bg-white/[0.05]">
                <summary className="cursor-pointer font-semibold text-sm text-violet-200 flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-violet-300" />
                  How does Jixxeo AI Chat work?
                </summary>
                <div className="mt-3 space-y-2.5 leading-relaxed">
                  <p><span className="font-bold text-white">1. Type a question.</span> Anything — news, weather, writing help, coding, "generate an image of…", "plan my week". Jixxeo picks the right skill automatically.</p>
                  <p><span className="font-bold text-white">2. Press the send arrow.</span> On the phone, Enter adds a new line — tap the arrow button on the right to send.</p>
                  <p><span className="font-bold text-white">3. The reply streams in live.</span> You'll see text appear word-by-word. Weather and image results appear inline.</p>
                  <p><span className="font-bold text-white">4. If it says "Could not reach Jixxeo AI":</span> your network dropped mid-reply. Tap <em>Send</em> again — the previous conversation stays open and context is kept.</p>
                  <p><span className="font-bold text-white">5. Daily caps.</span> Free members have a daily chat limit — upgrade to Premium or wait until midnight UTC for a reset.</p>
                  <p className="text-slate-400">Tip: tap the <span className="font-bold text-slate-200">+</span> button in the header to start a brand-new conversation whenever you want a clean slate.</p>
                </div>
              </details>
            </div>
          )}
          {messages.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} className="flex justify-end">
                <div data-testid="msg-user" className="max-w-[85%] px-4 py-2.5 rounded-2xl gradient-brand text-white rounded-br-sm">
                  {m.image_urls && m.image_urls.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {m.image_urls.map((u, k) => <img key={k} src={u} alt="" className="w-24 h-24 object-cover rounded-md border border-white/20" />)}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.text}</div>
                </div>
              </div>
            ) : (
              <AssistantMessage key={i} text={m.text} sources={m.sources} weather={m.weather} image={m.image} />
            )
          ))}
          {streamText && <AssistantMessage text={streamText + '▋'} sources={streamSources} weather={streamWeather} image={streamImage} />}
          {streaming && !streamText && (
            <ThinkingIndicator label={thinkingLabel} startedAt={thinkingStartedAt} />
          )}
        </div>

        {/* Composer */}
        <div
          className="border-t border-white/10 p-3 bg-slate-950/50 backdrop-blur-xl"
          style={{ paddingBottom: `calc(0.75rem + ${kbInset}px + env(safe-area-inset-bottom, 0px))` }}
        >
          {uploadingLabel && (
            <div data-testid="attach-uploading" className="flex items-center gap-2 mb-2 text-xs text-violet-200">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {uploadingLabel}
            </div>
          )}
          {attachments.length > 0 && (
            <div data-testid="attach-preview" className="flex flex-wrap gap-2 mb-2">
              {attachments.map((a, i) => (
                <div key={i} className="relative group" data-testid={`attach-item-${i}`}>
                  {a.kind === 'image' && (
                    <img src={a.data_url} alt={a.name} className="w-20 h-20 object-cover rounded-lg border border-white/10" />
                  )}
                  {a.kind === 'video' && (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-emerald-500/40 bg-slate-900">
                      {a.thumbnail && <img src={a.thumbnail} alt={a.name} className="w-full h-full object-cover" />}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                          <Video className="w-3.5 h-3.5 text-white" />
                        </div>
                      </div>
                      {a.duration && (
                        <div className="absolute bottom-0.5 right-1 text-[9px] font-bold text-white bg-black/70 px-1 rounded">
                          {a.duration.toFixed(1)}s
                        </div>
                      )}
                    </div>
                  )}
                  {a.kind === 'text' && (
                    <div className="w-20 h-20 rounded-lg border border-cyan-500/40 bg-cyan-500/10 flex flex-col items-center justify-center p-1 text-center">
                      <FileText className="w-6 h-6 text-cyan-300" />
                      <div className="text-[9px] text-cyan-200 truncate w-full">{a.name}</div>
                    </div>
                  )}
                  <button
                    data-testid={`attach-remove-${i}`}
                    onClick={() => removeAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow"
                    aria-label="Remove"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-2">
            {/* Attach + menu */}
            {user && (
              <>
                <div className="relative shrink-0">
                  <Button
                    data-testid="attach-btn"
                    type="button" size="icon" variant="outline"
                    onClick={() => setAttachMenuOpen(o => !o)}
                    title="Add attachment"
                    aria-label="Add attachment"
                    className={`shrink-0 ${attachMenuOpen ? 'bg-violet-500/20 border-violet-400 text-violet-200' : ''}`}
                  ><Plus className={`w-5 h-5 transition-transform ${attachMenuOpen ? 'rotate-45' : ''}`} /></Button>

                  {attachMenuOpen && (
                    <>
                      {/* Click-outside overlay */}
                      <div className="fixed inset-0 z-30" onClick={() => setAttachMenuOpen(false)} />
                      <div
                        data-testid="attach-menu"
                        className="absolute bottom-12 left-0 w-56 z-40 glass rounded-2xl border border-white/10 p-1 shadow-xl"
                      >
                        <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Add to Jixxeo AI</div>
                        <button data-testid="attach-camera" onClick={() => { setAttachMenuOpen(false); cameraInputRef.current?.click(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                          <Camera className="w-4 h-4 text-fuchsia-400" /> Take a Photo
                        </button>
                        <button data-testid="attach-gallery" onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                          <ImageIcon className="w-4 h-4 text-violet-400" /> Choose Photo
                        </button>
                        <button data-testid="attach-record-video" onClick={() => { setAttachMenuOpen(false); videoCamRef.current?.click(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                          <Video className="w-4 h-4 text-emerald-400" /> Record Video
                        </button>
                        <button data-testid="attach-video" onClick={() => { setAttachMenuOpen(false); videoFileRef.current?.click(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                          <Film className="w-4 h-4 text-cyan-400" /> Choose Video
                        </button>
                        <button data-testid="attach-file" onClick={() => { setAttachMenuOpen(false); fileAnyRef.current?.click(); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 text-sm">
                          <Paperclip className="w-4 h-4 text-slate-300" /> Choose File
                        </button>
                        <button onClick={() => setAttachMenuOpen(false)}
                          className="w-full text-center px-3 py-2 text-xs text-slate-500 hover:text-slate-200 border-t border-white/5 mt-1">
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Hidden inputs — one per capture kind so Android gets the right picker */}
                <input ref={fileInputRef} type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
                  multiple onChange={handleFiles} data-testid="attach-file-input" className="hidden" />
                <input ref={cameraInputRef} type="file"
                  accept="image/*" capture="environment"
                  onChange={handleFiles} data-testid="attach-camera-input" className="hidden" />
                <input ref={videoFileRef} type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-matroska,video/*"
                  onChange={handleVideoFiles} data-testid="attach-video-input" className="hidden" />
                <input ref={videoCamRef} type="file"
                  accept="video/*" capture="environment"
                  onChange={handleVideoFiles} data-testid="attach-video-cam-input" className="hidden" />
                <input ref={fileAnyRef} type="file"
                  onChange={handleAnyFile} data-testid="attach-any-input" className="hidden" />
              </>
            )}

            {/* Web search toggle */}
            {user && (
              <Button
                data-testid="web-search-toggle"
                type="button" size="icon"
                variant={webSearch ? 'default' : 'outline'}
                onClick={() => setWebSearch(w => !w)}
                title={webSearch ? 'Web search ON' : 'Enable live web search'}
                className={`shrink-0 ${webSearch ? 'bg-cyan-600 hover:bg-cyan-500 text-white border-cyan-500' : ''}`}
              ><Globe className="w-4 h-4" /></Button>
            )}

            {/* Record */}
            {recorder?.enabled && user && (
              <Button
                data-testid="chat-record-btn"
                type="button" size="icon"
                variant={recorder.state === 'recording' || recorder.state === 'paused' ? 'default' : 'outline'}
                onClick={() => (recorder.state === 'recording' || recorder.state === 'paused') ? recorder.stopAndPreview() : recorder.setShowPicker(true)}
                title={recorder.state === 'recording' ? 'Stop recording' : 'Record this session'}
                className={`shrink-0 ${recorder.state === 'recording' || recorder.state === 'paused' ? 'bg-red-600 hover:bg-red-500 text-white border-red-500' : ''}`}
              >
                {recorder.state === 'recording' || recorder.state === 'paused'
                  ? <Square className="w-4 h-4" />
                  : <Circle className="w-4 h-4 text-red-500 fill-red-500" />}
              </Button>
            )}

            {/* Composer textarea */}
            <textarea
              ref={textareaRef}
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={cfg.placeholder}
              rows={1}
              style={{ maxHeight: 240 }}
              className="flex-1 resize-none min-h-[44px] px-3 py-2.5 rounded-xl bg-slate-900/60 border border-white/10 focus:border-violet-500 outline-none text-sm leading-relaxed placeholder:text-slate-500 overflow-y-auto"
            />

            <Button
              data-testid="chat-send"
              onClick={send}
              disabled={streaming || (!input.trim() && attachments.length === 0)}
              className="gradient-brand text-white shrink-0 self-end h-11 w-11 p-0"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1.5 text-center">
            <kbd className="px-1 py-0.5 bg-white/5 rounded border border-white/10">Enter</kbd> for new line · <kbd className="px-1 py-0.5 bg-white/5 rounded border border-white/10">Ctrl + Enter</kbd> to send
            {webSearch && <span className="ml-2 text-cyan-300">· Web search ON</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}
